import { Button, Select, SelectItem, Switch } from "@heroui/react";
import { Trans, useLingui } from "@lingui/react/macro";
import { UseFormReturnType } from "@mantine/form";
import { FormValues } from "../../types";
import LimitationsSection from "./LimitationsSection";
import { getLocalizedName } from "@/utils";
import { LimitationTimeframe } from "@/server-types";
import { api } from "@/lib/api-client";

const ServicesTab = ({ form }: { form: UseFormReturnType<FormValues> }) => {
  const { t } = useLingui();
  const { data: servicesData = { services: [], total: 0 } } =
    api.service.useGetServices({
      enabled: true,
    });

  const addEmptyService = () => {
    form.insertListItem("subscriptionServices", {
      serviceId: -1,
      service: {
        nameLocalized: [],
      },
      limitEntries: false,
      limitations: [],
    });
  };

  const handleServiceSelect = (value: string, index: number) => {
    const selectedService = servicesData.services.find(
      (s) => s.serviceId === parseInt(value),
    );
    if (selectedService) {
      form.setFieldValue(`subscriptionServices.${index}`, {
        serviceId: selectedService.serviceId,
        service: {
          nameLocalized: selectedService.nameLocalized,
        },
        limitEntries: false,
        limitations: [],
      });
    }
  };

  const handleAddLimitation = (index: number) => {
    const currentLimitations =
      form.values.subscriptionServices[index].limitations || [];
    const usedTimeframes = new Set(
      currentLimitations.map((limitation) => limitation.timeframe),
    );

    const limitationTimeframes = ["day", "week", "month", "year", "all"];

    const unusedTimeframe =
      limitationTimeframes.find(
        (timeframe) => !usedTimeframes.has(timeframe),
      ) || "month";

    const newLimitation = {
      timeframe: unusedTimeframe,
      limitAmount: 30,
    };

    form.setFieldValue(`subscriptionServices.${index}.limitations`, [
      ...currentLimitations,
      newLimitation,
    ]);
  };

  const handleRemoveLimitation = (
    parentIndex: number,
    limitationIndex: number,
  ) => {
    const currentLimitations = [
      ...form.values.subscriptionServices[parentIndex].limitations,
    ];
    currentLimitations.splice(limitationIndex, 1);
    form.setFieldValue(
      `subscriptionServices.${parentIndex}.limitations`,
      currentLimitations,
    );
  };

  const handleUpdateLimitation = (
    parentIndex: number,
    limitationIndex: number,
    field: "timeframe" | "limitAmount",
    value: LimitationTimeframe | number,
  ) => {
    form.setFieldValue(
      `subscriptionServices.${parentIndex}.limitations.${limitationIndex}.${field}`,
      value,
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {form.values.subscriptionServices.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-sm text-gray-600">
            <Trans>
              Brez storitev. Kliknite gumb spodaj za dodajanje prve storitve.
            </Trans>
          </p>
          <Button size="sm" onPress={addEmptyService}>
            <Trans>Dodaj storitev</Trans>
          </Button>
        </div>
      ) : (
        <>
          {form.values.subscriptionServices.map(
            (subscriptionService, index) => (
              <div
                key={index}
                className="relative rounded-lg border border-gray-200 p-4 shadow-sm"
              >
                <div className="mb-4">
                  <Button
                    size="sm"
                    variant="bordered"
                    className="absolute right-2 top-2"
                    onPress={() =>
                      form.removeListItem("subscriptionServices", index)
                    }
                  >
                    <Trans>Izbriši</Trans>
                  </Button>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <Select
                      label={t`Storitev`}
                      labelPlacement="outside"
                      className="flex-1"
                      placeholder={t`Izberi storitev`}
                      selectedKeys={[subscriptionService.serviceId.toString()]}
                      variant="bordered"
                      data-identifier="subscription-service-select"
                      onChange={(e) =>
                        handleServiceSelect(e.target.value, index)
                      }
                    >
                      <>
                        {servicesData.services.map((s) => (
                          <SelectItem
                            key={s.serviceId.toString()}
                            textValue={getLocalizedName(s.nameLocalized)}
                          >
                            {getLocalizedName(s.nameLocalized)}
                          </SelectItem>
                        ))}
                      </>
                    </Select>
                  </div>
                  {subscriptionService.serviceId > 0 && (
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          size="sm"
                          isSelected={subscriptionService.limitEntries}
                          data-identifier="subscription-service-limit-entries"
                          onValueChange={(value) =>
                            form.setFieldValue(
                              `subscriptionServices.${index}.limitEntries`,
                              value,
                            )
                          }
                        >
                          <Trans>Omeji vstope</Trans>
                        </Switch>
                      </div>
                      {subscriptionService.limitEntries && (
                        <LimitationsSection
                          parentIndex={index}
                          limitations={subscriptionService.limitations}
                          onAddLimitation={handleAddLimitation}
                          onRemoveLimitation={handleRemoveLimitation}
                          onUpdateLimitation={handleUpdateLimitation}
                          data-identifier="subscription-service-limitations"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ),
          )}
          <div className="flex justify-end">
            <Button size="sm" onPress={addEmptyService}>
              <Trans>Dodaj storitev</Trans>
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ServicesTab;
